// ./src/utils/homeOverview.ts
//
// WHAT THE HOME PAGE SAYS NEEDS DOING TODAY — the rules, as pure functions.
//
// ⚠⚠ EVERY WAY THIS PAGE CAN BE WRONG LOOKS LIKE GOOD NEWS. A count that fails to load renders as
// nothing; a source that never answers renders as nothing; a zero renders as nothing. All three are
// indistinguishable on screen from "you have no outstanding work" — which is the one sentence Home
// exists to say, and the one sentence nobody re-checks. So the rules live here, runnable, and
// `npm run check-home` RUNS them: a green Home page proves nothing on its own.
//
/**
 * ⚠⚠ NO RUNTIME IMPORT FROM THE SERVICE LAYER, DELIBERATELY — same reason as
 * `complianceSetup.ts`: a guard has to `import` this outside the browser, and pulling in a service
 * drags in `config/branding`, which reads `import.meta.env` and dies the moment anything but Vite
 * loads it. The shapes below are local mirrors; nothing here is erased at build time by accident.
 */

// =============================================================================
// Shapes
// =============================================================================

/** An event, as much of one as any rule here reads. Mirrors the fields on `Event`. */
export interface EventLike {
  id?: string | null;
  title?: string | null;
  eventStatus?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /** A RECURRING event's LAST date — its own `endsAt` is only its first date's. */
  seriesEndsAt?: string | null;
  /** A RECURRING event's next date not over yet. */
  nextDateStartsAt?: string | null;
  isAllDay?: boolean | null;
}

/** Mirrors the `canceled` lifecycle value used by `EventCard` and the event dashboard. */
export const EVENT_STATUS_CANCELED = "canceled";

/** Mirrors the `published` lifecycle value. */
export const EVENT_STATUS_PUBLISHED = "published";

const ms = (value?: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);

  return Number.isNaN(parsed) ? null : parsed;
};

const nowMs = (now: Date | string | number): number => {
  if (now instanceof Date) {
    return now.getTime();
  }

  if (typeof now === "number") {
    return now;
  }

  const parsed = Date.parse(now);

  return Number.isNaN(parsed) ? Date.now() : parsed;
};

/**
 * When an event is NEXT ON: a recurring series' next date, else its start.
 * ⚠ Mirrors `eventListHelpers.nextOn` on purpose — Home and All Events must not be able to disagree
 * about which event is the near one.
 */
export const eventNextOn = (event: EventLike): number | null => ms(event.nextDateStartsAt) ?? ms(event.startsAt);

/** The moment an event stops being current: a series' last date, else its end, else its start. */
export const eventEffectiveEnd = (event: EventLike): number | null =>
  ms(event.seriesEndsAt) ?? ms(event.endsAt) ?? ms(event.startsAt);

// =============================================================================
// The spotlight — which single event Home leads with
// =============================================================================

export type SpotlightKind = "now" | "next" | "planning" | "none";

export interface SpotlightPick {
  event: EventLike | null;
  kind: SpotlightKind;
}

/**
 * Pick the ONE event Home leads with, out of the already-split `upcoming` list.
 *
 * ⚠⚠ `upcoming[0]` IS THE WRONG ANSWER, and it is the obvious one. `splitEvents` sorts UNDATED
 * events to the TOP (they are "being planned", and the All Events list wants them first) — so a
 * half-finished draft from three months ago outranks the wedding on Saturday, and Home leads with
 * the one event nothing is happening about. A dateless draft is the LAST resort here, not the first.
 *
 * ⚠ An event under way beats one that is merely soonest: during the event is the only time Home's
 * answer to "what needs me today" is literally the event itself.
 */
export const pickSpotlightEvent = (upcoming: EventLike[], now: Date | string | number = new Date()): SpotlightPick => {
  const at = nowMs(now);
  const live = (upcoming ?? []).filter((event) => event && event.eventStatus !== EVENT_STATUS_CANCELED);

  const ongoing = live
    .filter((event) => {
      const start = eventNextOn(event);
      const end = eventEffectiveEnd(event);

      return start != null && end != null && start <= at && end >= at;
    })
    .sort((a, b) => (eventNextOn(a) ?? 0) - (eventNextOn(b) ?? 0));

  if (ongoing.length > 0) {
    return { event: ongoing[0]!, kind: "now" };
  }

  const dated = live
    .filter((event) => eventNextOn(event) != null)
    .sort((a, b) => (eventNextOn(a) ?? 0) - (eventNextOn(b) ?? 0));

  if (dated.length > 0) {
    return { event: dated[0]!, kind: "next" };
  }

  const undated = live.find((event) => eventNextOn(event) == null) ?? null;

  return undated ? { event: undated, kind: "planning" } : { event: null, kind: "none" };
};

export type CountdownKind = "now" | "today" | "tomorrow" | "days" | "unscheduled";

export interface Countdown {
  kind: CountdownKind;
  /** Whole days until it starts. 0 when it is today or under way. */
  days: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Calendar days between two instants, by LOCAL day — not by 24-hour blocks. */
const calendarDaysBetween = (fromMs: number, toMs: number): number => {
  const from = new Date(fromMs);
  const to = new Date(toMs);
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();

  return Math.round((toDay - fromDay) / DAY_MS);
};

/**
 * How far off an event is.
 *
 * ⚠ COUNTED IN CALENDAR DAYS, NOT IN 24-HOUR BLOCKS. An event at 9am tomorrow is 15 hours away,
 * which floors to "today" on a 24-hour count — and a host reading "today" about tomorrow will not
 * check again. Same trap the travel report paid for: a day is where the clock says it is.
 */
export const eventCountdown = (event: EventLike | null, now: Date | string | number = new Date()): Countdown => {
  if (!event) {
    return { kind: "unscheduled", days: 0 };
  }

  const at = nowMs(now);
  const start = eventNextOn(event);
  const end = eventEffectiveEnd(event);

  if (start == null) {
    return { kind: "unscheduled", days: 0 };
  }

  if (start <= at && end != null && end >= at) {
    return { kind: "now", days: 0 };
  }

  const days = calendarDaysBetween(at, start);

  if (days <= 0) {
    return { kind: "today", days: 0 };
  }

  if (days === 1) {
    return { kind: "tomorrow", days: 1 };
  }

  return { kind: "days", days };
};

// =============================================================================
// Needs attention
// =============================================================================

/**
 * ⚠⚠ EVERY ROW COMES FROM THE SERVER, AND THAT IS THE POINT. Guest requests, sponsor applications and
 * volunteer shifts were once read on the client and rendered as ONE row holding the total across every
 * event, linking to whichever event the oldest one was on — while every row beside them was a count
 * for ONE event. Two meanings for a row, side by side, with nothing on screen to tell them apart.
 *
 * ⚠ The empty-list version of this rule was kept briefly "in case a client source comes back": dead
 * code that a mutation could rewrite without any check noticing. That is a worse thing to own than the
 * flexibility was worth, so it is gone.
 */

/**
 * ONE ROW ON THE CARD, from either half.
 *
 * ⚠ THE TWO HALVES ARE NOT THE SAME KIND OF QUESTION, which is why both exist. The three CLIENT
 * sources are pure status filters ("who is waiting on a decision") and the API can already answer
 * them exactly. Everything else is a DATE WINDOW — due in three days, landing tomorrow, lapsing
 * tonight — and no filter in this API reaches a date, so asking the client to narrow a page locally
 * produces an empty, correct-looking card. Those live in `myHomeAttention`, computed in SQL.
 */
export interface AttentionItem {
  /** A client `AttentionKey`, or a server kind from `boundary/homeAttention.ts`. */
  kind: string;
  /** Always 'server' today — kept so a row's origin is stated, not assumed. */
  source: 'server';
  severity: string;
  count: number;
  eventId: string | null;
  eventTitle?: string | null;
  at?: string | null;
  who?: string | null;
  amount?: string | null;
  currencyCode?: string | null;
  capacity?: number | null;
  /** The threshold this row was judged by (a share), so the copy can name the host's own number. */
  threshold?: number | null;
  /** For an underfunded event: the share of projected cost that projected revenue covers. */
  coverage?: number | null;
  reasons?: string[] | null;
}

/** What the server query answered, as the client holds it. */
export interface ServerAttention {
  items: AttentionItem[];
  moreCount: number;
  /** Sources the SERVER could not answer. Never rendered as "nothing to report". */
  failedKinds: string[];
  /** The query came back at all. `false` = still in flight, or it failed. */
  answered: boolean;
  /** It came back and could not be trusted. */
  failed: boolean;
}

export const EMPTY_SERVER_ATTENTION: ServerAttention = { items: [], moreCount: 0, failedKinds: [], answered: false, failed: false };

export const ATTENTION_SEVERITY = { URGENT: 'urgent', WARN: 'warn', INFO: 'info' } as const;

const SEVERITY_RANK: Record<string, number> = {
  [ATTENTION_SEVERITY.URGENT]: 0,
  [ATTENTION_SEVERITY.WARN]: 1,
  [ATTENTION_SEVERITY.INFO]: 2,
};

export interface AttentionSummary {
  items: AttentionItem[];
  /** Everything answered, and every answer was zero. The ONLY state that may say "all caught up". */
  allClear: boolean;
  /** At least one source could not answer. Never rendered as zero. */
  incomplete: boolean;
  /** At least one source is still in flight. */
  loading: boolean;
  total: number;
  /** Items the server dropped past its cap. */
  moreCount: number;
  /** The server answered at all (it may still have named sources in `failedKinds`). */
  answered: boolean;
  /** The server's own sources that could not answer. */
  failedKinds: string[];
}

/**
 * Turn the readings into what the card renders.
 *
 * ⚠⚠ "ALL CAUGHT UP" REQUIRES THAT EVERY SOURCE ACTUALLY ANSWERED. A failed read defaulting to 0 is
 * the single most expensive thing this page can do: it draws a green tick over an unworked queue,
 * and unlike an error it gives the host a REASON not to look. A source that did not answer makes the
 * card say so — it never makes it say "nothing to do".
 *
 * ⚠ The inverse is checked too. A rule that reported "some checks could not load" whenever anything
 * was merely still in flight would put a permanent warning on the first screen of the product, which
 * is how a host learns to ignore this card entirely.
 */
export const summarizeAttention = (server: ServerAttention = EMPTY_SERVER_ATTENTION): AttentionSummary => {
  const items: AttentionItem[] = [];
  let incomplete = false;
  let loading = false;
  let total = 0;

  /**
   * ⚠⚠ THE SERVER HALF COUNTS TOWARDS THE ALL-CLEAR TOO. A query that has not come back, or came
   * back broken, means we do not know whether an invoice is due tomorrow — and "we do not know" must
   * never render as the green tick. `failedKinds` is the server saying which of ITS OWN sources went
   * quiet; a non-empty list is the same "incomplete" state as a failed client read.
   */
  if (server.failed) {
    incomplete = true;
  } else if (!server.answered) {
    loading = true;
  } else {
    items.push(...server.items);
    total += server.items.reduce((sum, item) => sum + (item.count || 0), 0);

    if (server.failedKinds.length > 0) {
      incomplete = true;
    }
  }

  // Severity first, then the soonest thing — the order a host reads them in.
  items.sort((a, b) => {
    const bySeverity = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);

    if (bySeverity !== 0) {
      return bySeverity;
    }

    const at = (value?: string | null) => (value ? Date.parse(value) : Number.MAX_SAFE_INTEGER);

    return at(a.at) - at(b.at);
  });

  return {
    items,
    allClear: !server.failed && server.answered && server.failedKinds.length === 0 && total === 0,
    incomplete,
    loading,
    total,
    moreCount: server.answered && !server.failed ? server.moreCount : 0,
    answered: server.answered && !server.failed,
    failedKinds: server.answered && !server.failed ? server.failedKinds : [],
  };
};

// =============================================================================
// Where an attention row goes
// =============================================================================

/**
 * ⚠ ONE DERIVATION PER DESTINATION, in one place. Paths assembled at the call site are how
 * `${origin}/${slug}` shipped a 404 to every ticket buyer twice in one hour; these are pinned
 * against `siteMap.ts` by `npm run check-home`, so a route that gets renamed fails a check instead
 * of rendering a dead row on the first screen of the product.
 */
export const ATTENTION_PATHS: Record<string, (eventId: string) => string> = {
  // Decisions waiting on the host — grouped per event in SQL, like everything else.
  guest_requests: (eventId) => `/events/${eventId}/guests`,
  sponsor_applications: (eventId) => `/events/${eventId}/partners/applications`,
  volunteer_shifts: (eventId) => `/events/${eventId}/volunteers/applications`,
  // The server's date-windowed kinds (ATTENTION_KINDS in the API's boundary/homeAttention.ts).
  weather_risk: (eventId) => `/events/${eventId}`,
  budget_due: (eventId) => `/events/${eventId}/budget`,
  travel_soon: (eventId) => `/events/${eventId}/travel`,
  rental_soon: (eventId) => `/events/${eventId}/rentals`,
  stay_soon: (eventId) => `/events/${eventId}/accommodations`,
  artist_pending: (eventId) => `/events/${eventId}/artists`,
  vendors_overdue: (eventId) => `/events/${eventId}/vendors`,
  vendors_unreachable: (eventId) => `/events/${eventId}/vendors`,
  vendors_email_undelivered: (eventId) => `/events/${eventId}/vendors`,
  artist_hold_expiring: (eventId) => `/events/${eventId}/artists`,
  registration_at_risk: (eventId) => `/events/${eventId}`,
  // ⚠ The FINANCE report, not the event page — this warning is about the money, and the P&L is both
  // where the host can see why and the source of the very numbers that raised it. Sending them to
  // the dashboard would make them go looking for the screen that explains a warning we just showed.
  event_underfunded: (eventId) => `/events/${eventId}/finance`,
};

/**
 * Kinds that belong to the ACCOUNT, not to an event — stock is one catalog across every event, so
 * there is no event page that could show it.
 *
 * ⚠ THEY MUST NOT FALL BACK TO THE EVENT LIST. A host sent to "All events" to find an expiring case
 * of wine has been given a worse version of no link at all.
 */
export const ACCOUNT_ATTENTION_PATHS: Record<string, string> = {
  inventory_expiring: '/library/inventory/stock',
  inventory_low: '/library/inventory/stock',
  // ROADMAP #161 b — one row per event that cannot take payment, but ONE fix for all of them: the account's Stripe.
  card_payments_off: '/money/stripe-connect',
};

/** Every event page the Home page links to, as a sitemap pattern. */
export const HOME_EVENT_ROUTES = [
  "/events/:eventId",
  "/events/:eventId/guests",
  "/events/:eventId/checkin",
  "/events/:eventId/website",
  "/events/:eventId/tasks",
  "/events/:eventId/partners/applications",
  "/events/:eventId/volunteers/applications",
  "/events/:eventId/budget",
  "/events/:eventId/travel",
  "/events/:eventId/rentals",
  "/events/:eventId/accommodations",
  "/events/:eventId/artists",
] as const;

/** The event list — the fallback when a reading carried a count but no row to point at. */
export const ALL_EVENTS_PATH = "/events/all";

export const attentionPath = (kind: string, eventId?: string | null): string => {
  const accountPath = ACCOUNT_ATTENTION_PATHS[kind];

  if (accountPath) {
    return accountPath;
  }

  const build: ((eventId: string) => string) | undefined = ATTENTION_PATHS[kind];

  return typeof build === 'function' && eventId ? build(eventId) : ALL_EVENTS_PATH;
};

/** An event page under the spotlight card. */
export const eventPath = (eventId: string, section?: "guests" | "checkin" | "website" | "tasks"): string =>
  section ? `/events/${eventId}/${section}` : `/events/${eventId}`;

// =============================================================================
// Getting started
// =============================================================================

export const CHECKLIST_KEYS = ["confirmEmail", "createEvent", "publishEvent", "getPaid", "saveVenue", "organizerPage"] as const;

export type ChecklistKey = (typeof CHECKLIST_KEYS)[number];

export interface ChecklistFacts {
  /**
   * ROADMAP #161 — the signed-in user's email is confirmed (ms_auth `emailConfirmation`); `null` = they have no address to
   * confirm. ⚠ Required, no default: a caller that forgot it would drop the step that brings an unconfirmed host back to
   * the account onboarding.
   */
  emailConfirmed: boolean | null;
  hasEvents: boolean;
  hasPublishedEvent: boolean;
  /** `null` = the payout status has not been read yet, or could not be. */
  payoutsReady: boolean | null;
  /**
   * ROADMAP #161 b — an upcoming event sells something paid that nobody can pay for (`sellsPaidFrom`). `null` = not known
   * (the attention read is out, or its card-payments source failed). ⚠ Required, no default: a caller that forgot it
   * would hide the step that decides whether the host can be paid at all.
   */
  sellsPaid: boolean | null;
  /** ROADMAP #161 d — the account runs its own space (`accountType` 'venue'); `null` = not read. */
  isVenueAccount: boolean | null;
  /** A default venue is saved (`account_preference.default_venue_id`); `null` = not read. */
  hasDefaultVenue: boolean | null;
  /**
   * ROADMAP #198 b + c — the account has an organizer page WITH a logo (the onboarding's "Your organizer page" step);
   * `null` = not read, or the read failed — the item is then left out rather than claimed either way. ⚠ Required, no
   * default: a caller that forgot it would drop the step a skipped onboarding comes back to.
   */
  organizerPageReady: boolean | null;
}

export interface ChecklistStep {
  key: ChecklistKey;
  done: boolean;
}

/**
 * The first-run steps, in the order they have to happen.
 *
 * ⚠ `payoutsReady === null` IS NOT DONE. An unread status counting as done hides the checklist — and
 * the step it hides is the one that decides whether the host can be paid at all. Unknown is
 * "not yet" for anything that gates money.
 */
export const buildChecklist = (facts: ChecklistFacts): ChecklistStep[] => [
  // #161 — first: the account onboarding's first step, resumed from here (it opens `/onboarding?step=email`).
  ...(facts.emailConfirmed === null ? [] : [{ key: "confirmEmail" as const, done: facts.emailConfirmed === true }]),
  { key: "createEvent", done: !!facts.hasEvents },
  { key: "publishEvent", done: !!facts.hasPublishedEvent },
  /**
   * #161 b (owner, 2026-10-15) — only for a host who SELLS something paid: telling a free birthday party to set up payouts
   * is a nag. Kept while it is unknown (unknown is "not yet" for money), and shown done once payouts are on.
   */
  ...(facts.sellsPaid !== false || facts.payoutsReady === true ? [{ key: "getPaid" as const, done: facts.payoutsReady === true }] : []),
  // #161 d — a venue saves its own space once, so every new event starts there. Not offered until the type is KNOWN.
  ...(facts.isVenueAccount === true ? [{ key: "saveVenue" as const, done: facts.hasDefaultVenue === true }] : []),
  // #198 b + c — every account type (owner, 2026-10-21: "all the types"): the public page its events show under, with its logo.
  ...(facts.organizerPageReady === null ? [] : [{ key: "organizerPage" as const, done: facts.organizerPageReady }]),
];

/**
 * #161 b — does the account sell something paid that nobody can pay for? From the server's attention list: a
 * `card_payments_off` row says yes; an answer without one says no; no answer, or that source failing, is NOT KNOWN.
 * ⓘ "No" also covers an account whose Stripe already takes charges (the server raises nothing then) — `payoutsReady`
 *   shows the step done for it.
 */
export const sellsPaidFrom = (summary: Pick<AttentionSummary, "items" | "answered" | "failedKinds">): boolean | null => {
  if (summary.items.some((item) => item.kind === "card_payments_off")) {
    return true;
  }

  if (!summary.answered || summary.failedKinds.includes("card_payments_off")) {
    return null;
  }

  return false;
};

/** Every step done — the card retires itself. */
export const checklistComplete = (steps: ChecklistStep[]): boolean => steps.every((step) => step.done);

/** Whether an event counts as published, for the checklist. */
export const hasPublishedEvent = (events: EventLike[]): boolean =>
  (events ?? []).some((event) => event?.eventStatus === EVENT_STATUS_PUBLISHED);
